import { PlaceholderPosition } from '@/types/quote';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Globe, Type } from 'lucide-react';

interface PlaceholderPropertyEditorProps {
  placeholder: PlaceholderPosition;
  onChange: (updated: Partial<PlaceholderPosition>) => void;
  onDelete: () => void;
}

export const PlaceholderPropertyEditor = ({
  placeholder,
  onChange,
  onDelete
}: PlaceholderPropertyEditorProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Veld Eigenschappen</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Static text indicator */}
      {placeholder.key.startsWith('{{static_text_') ? (
        <div className="p-2 bg-orange-500/10 border border-orange-500/30 rounded flex items-center gap-2">
          <Type className="h-4 w-4 text-orange-600" />
          <span className="text-xs text-orange-700 font-medium">Vaste Tekst</span>
        </div>
      ) : (
        <div className="space-y-2 text-xs">
          <div className="p-2 bg-muted rounded">
            <code className="break-all">{placeholder.key}</code>
          </div>
        </div>
      )}

      {/* Static text value editor */}
      {placeholder.key.startsWith('{{static_text_') && (
        <div className="space-y-2">
          <Label className="text-xs">Tekst</Label>
          <Input
            value={placeholder.staticValue || ''}
            onChange={(e) => onChange({ staticValue: e.target.value })}
            placeholder="Voer tekst in..."
          />
        </div>
      )}

      {/* Global Toggle */}
      <div className="flex items-center justify-between py-2 px-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
        <Label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <Globe className="h-3.5 w-3.5 text-amber-600" />
          <span>Globaal (alle pagina's)</span>
        </Label>
        <Switch
          checked={placeholder.isGlobal || false}
          onCheckedChange={(checked) => onChange({ isGlobal: checked })}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Pagina</Label>
        <Input
          type="number"
          min={1}
          value={placeholder.page}
          onChange={(e) => onChange({ page: parseInt(e.target.value) || 1 })}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label className="text-xs">X Positie</Label>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ x: Math.max(0, placeholder.x - 1) })}
              className="h-10 w-10 p-0"
            >
              -
            </Button>
            <Input
              type="number"
              value={Math.round(placeholder.x)}
              onChange={(e) => onChange({ x: parseInt(e.target.value) || 0 })}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ x: placeholder.x + 1 })}
              className="h-10 w-10 p-0"
            >
              +
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Y Positie</Label>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ y: Math.max(0, placeholder.y - 1) })}
              className="h-10 w-10 p-0"
            >
              -
            </Button>
            <Input
              type="number"
              value={Math.round(placeholder.y)}
              onChange={(e) => onChange({ y: parseInt(e.target.value) || 0 })}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ y: placeholder.y + 1 })}
              className="h-10 w-10 p-0"
            >
              +
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label className="text-xs">Breedte</Label>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ width: Math.max(10, placeholder.width - 1) })}
              className="h-10 w-10 p-0"
            >
              -
            </Button>
            <Input
              type="number"
              value={Math.round(placeholder.width)}
              onChange={(e) => onChange({ width: parseInt(e.target.value) || 100 })}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ width: placeholder.width + 1 })}
              className="h-10 w-10 p-0"
            >
              +
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Hoogte</Label>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ height: Math.max(10, placeholder.height - 1) })}
              className="h-10 w-10 p-0"
            >
              -
            </Button>
            <Input
              type="number"
              value={Math.round(placeholder.height)}
              onChange={(e) => onChange({ height: parseInt(e.target.value) || 30 })}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ height: placeholder.height + 1 })}
              className="h-10 w-10 p-0"
            >
              +
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Lettergrootte</Label>
        <Input
          type="number"
          min={8}
          max={72}
          value={placeholder.fontSize}
          onChange={(e) => onChange({ fontSize: parseInt(e.target.value) || 12 })}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Tekstdikte</Label>
        <Select
          value={placeholder.fontWeight}
          onValueChange={(value: 'normal' | 'bold') => onChange({ fontWeight: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">Normaal</SelectItem>
            <SelectItem value="bold">Vet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Uitlijning</Label>
        <Select
          value={placeholder.align}
          onValueChange={(value: 'left' | 'center' | 'right') => onChange({ align: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Links</SelectItem>
            <SelectItem value="center">Midden</SelectItem>
            <SelectItem value="right">Rechts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Kleur</Label>
        <div className="flex gap-2">
          <Input
            type="color"
            value={placeholder.color}
            onChange={(e) => onChange({ color: e.target.value })}
            className="w-16 h-10 p-1"
          />
          <Input
            value={placeholder.color}
            onChange={(e) => onChange({ color: e.target.value })}
            placeholder="#000000"
          />
        </div>
      </div>
    </div>
  );
};
